/**
 * Chromium's native "Install app" prompt.
 *
 * `beforeinstallprompt` fires once per page load, usually before anyone has
 * navigated to a page that could offer an install button, so it's captured at
 * boot and held here. The browser's own install hint is left alone everywhere
 * except the mobile guide, which offers its own button instead.
 */
import { ref } from 'vue'

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const deferred = ref<BeforeInstallPromptEvent | null>(null)
const installed = ref(false)

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (event) => {
        if (window.location.pathname.startsWith('/mobile')) event.preventDefault()
        deferred.value = event as BeforeInstallPromptEvent
    })
    window.addEventListener('appinstalled', () => {
        installed.value = true
        deferred.value = null
    })
}

export function useInstallPrompt() {
    /** Resolves true when the creator accepted the browser's install dialog. */
    async function promptInstall(): Promise<boolean> {
        const event = deferred.value
        if (!event) return false
        deferred.value = null
        try {
            await event.prompt()
            const choice = await event.userChoice
            return choice.outcome === 'accepted'
        } catch {
            return false
        }
    }
    return { canPrompt: deferred, installed, promptInstall }
}
